package com.essntl.features.proposal.ui.addeditproposal.components

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.isCtrlPressed
import androidx.compose.ui.input.key.isMetaPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import com.essntl.core.utils.utils.formatIntMoney
import com.essntl.features.category.domain.model.CategoryModel
import com.essntl.features.price.domain.model.proposalserviceprice.ProposalServicePriceModel
import com.essntl.features.service.domain.model.service.ServiceModel
import com.essntl.uikit.components.button.tinted.EsSmallTintedButton
import com.essntl.uikit.components.dropdown.EsDropDownMenu
import com.essntl.uikit.components.dropdown.EsDropdownMenuItem
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.image.AsyncUrlIcon
import com.essntl.uikit.components.tooltip.EsTooltip
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.key.LocalKeyboardState
import com.essntl.uikit.theme.Accent200
import com.essntl.uikit.theme.Primary500
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.config.CurrencyConfig

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun ProposalServiceCard(
    service: ServiceModel,
    category: CategoryModel? = null,
    isTotalOnlyProposal: Boolean = false,
    isPaid: Boolean = false,
    loading: Boolean = false,
    isSetAsPaidEnabled: Boolean = false,
    isInGroupSelection: Boolean = false,
    isSelected: Boolean = false,
    isEnabled: Boolean = true,
    hideOnProposal: Boolean = false,
    price: Int? = null,
    priceCurrency: String? = null,
    proposalServicePrices: List<ProposalServicePriceModel> = emptyList(),
    dynamicPriceEnabled: Boolean = false,
    onSelect: () -> Unit = {},
    onDoubleClick: () -> Unit = {},
    onAddToGroup: () -> Unit = {},
    modifier: Modifier = Modifier,
    onDeleteProposalService: () -> Unit = {},
    onDuplicateProposalService: () -> Unit = {},
    onMarkPaid: () -> Unit = {},
    onHideOnProposal: () -> Unit = {},
    onNavigateToItinerary: () -> Unit = {},
    onSetPrice: () -> Unit = {},
) {
    val keyboardState = LocalKeyboardState.current

    var isDropDownExpanded by remember { mutableStateOf(false) }

    LaunchedEffect(keyboardState.pressedKey, isSelected, isPaid) {
        if (isPaid || !isEnabled) return@LaunchedEffect
        val event = keyboardState.pressedKey ?: return@LaunchedEffect

        if (isSelected && event.key == Key.Backspace)
            onDeleteProposalService()

        if (
            isSelected &&
            (event.isCtrlPressed || event.isMetaPressed) &&
            event.key == Key.D
        )
            onDuplicateProposalService()
    }

    Column(
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(2.5f.spacing),
            modifier = modifier
                .border(
                    width = 3.dp,
                    color = when {
                        isInGroupSelection -> MaterialTheme.esColorScheme.borderAccent
                        isSelected -> MaterialTheme.esColorScheme.borderWarning
                        else -> Color.Transparent
                    },
                    shape = RoundedCornerShape(2.spacing),
                )
                .clip(RoundedCornerShape(2.spacing))
                .combinedClickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = {
                        val pressedKey = keyboardState.pressedKey
                        if (
                            pressedKey != null &&
                            (pressedKey.isCtrlPressed ||
                                pressedKey.isMetaPressed)
                        ) {
                            if (isEnabled)
                                onAddToGroup()
                        } else {
                            onSelect()
                        }
                    },
                    onDoubleClick = {
                        if (isEnabled)
                            onDoubleClick()
                    },
                )
                .background(
                    color = if (isPaid) Color(0xFFCDC8F9)
                            else if (hideOnProposal) Accent200.copy(0.5f)
                            else Accent200,
                )
                .padding(1.spacing) // Reduced from 1.5.spacing
                .width(226.dp),
        ) {
            Column(
                verticalArrangement = Arrangement.spacedBy(0.5.spacing), // Added compact spacing
            ) {
                Box(
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Text(
                        text = service.title,
                        style = MaterialTheme.esTypography.p2Regular14,
                        color = if (isPaid)
                            Primary500
                        else if (hideOnProposal)
                            MaterialTheme.esColorScheme.textPrimary.copy(0.7f)
                        else
                            MaterialTheme.esColorScheme.textPrimary,
                        overflow = TextOverflow.Ellipsis,
                        minLines = 1, // Changed from 2
                        maxLines = 1, // Changed from 2
                        modifier = Modifier.align(Alignment.TopStart),
                    )

                    val iconUrl = category?.iconUrl
                    if (!iconUrl.isNullOrBlank())
                        AsyncUrlIcon(
                            url = category.iconUrl ?: "",
                            contentDescription = category.name,
                            tint = Color(0xFF8985A2),
                            modifier = Modifier
                                .size(18.dp) // Reduced from 22.dp
                                .align(Alignment.TopEnd),
                        )
                    else
                        Box(
                            modifier = Modifier
                                .size(18.dp) // Reduced from 22.dp
                                .align(Alignment.CenterEnd),
                        )
                }

                Box(
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    if (isPaid)
                        EsTooltip(
                            text = "Edit in itinerary",
                            modifier = Modifier.align(Alignment.TopEnd),
                        ) {
                            EsSmallOutlineIconButton(
                                onClick = onNavigateToItinerary,
                                iconRes = EsIcons.Itinerary,
                            )
                        }
                    else {
                        EsSmallTintedButton(
                            text = when {
                                (dynamicPriceEnabled && (proposalServicePrices.isEmpty() || proposalServicePrices.all { it.visibility == ProposalServicePriceModel.Visibility.Hidden })) ||
                                    (!dynamicPriceEnabled && (price == null && priceCurrency == null))
                                    -> "Add price"

                                else -> {
                                    if (dynamicPriceEnabled) {
                                        val filteredPrices = proposalServicePrices
                                            .filter { ps -> ps.visibility != ProposalServicePriceModel.Visibility.Hidden }
                                            .sortedBy { it.total?.toInt() ?: 0 }

                                        if (filteredPrices.isNotEmpty()) {
                                            val pricesToShow = if (filteredPrices.size > 1) {
                                                listOf(filteredPrices.first(), filteredPrices.last())
                                            } else {
                                                filteredPrices
                                            }

                                            pricesToShow.joinToString(" - ") {
                                                CurrencyConfig.defaultCurrencySymbol +
                                                    formatIntMoney(
                                                        it.total?.toInt() ?: 0,
                                                    )
                                            }
                                        } else {
                                            "Add price"
                                        }
                                    }
                                    else
                                        "${priceCurrency ?: CurrencyConfig.defaultCurrencySymbol}${formatIntMoney(price ?: 0)}"
                                }
                            },
                            onClick = { onSetPrice() },
                            enabled = isEnabled,
                            contentPadding = PaddingValues(horizontal = 8.dp, vertical = 0.spacing),
                            height = 28.dp,
                            loading = loading,
                            modifier = Modifier.align(Alignment.BottomStart),
                        )

                        EsLargeIcon(
                            res = EsIcons.MoreVertical,
                            modifier = Modifier
                                .align(Alignment.TopEnd)
                                .clip(CircleShape)
                                .pointerHoverIcon(PointerIcon.Hand)
                                .clickable(
                                    enabled = isEnabled,
                                ) {
                                    isDropDownExpanded = true
                                },
                        )
                    }
                }
            }
        }

        EsDropDownMenu(
            expanded = isDropDownExpanded,
            onDismissRequest = { isDropDownExpanded = false },
            offset = DpOffset(
                x = 60.dp,
                y = 4.dp,
            ),
            modifier = Modifier
                .width(190.dp),
        ) {
            EsDropdownMenuItem(
                text = "Edit",
                prefixIcon = EsIcons.Edit,
                iconColor = MaterialTheme.esColorScheme.iconPrimary,
                onClick = {
                    isDropDownExpanded = false
                    val pressedKey = keyboardState.pressedKey
                    if (
                        pressedKey != null &&
                        (pressedKey.isCtrlPressed ||
                            pressedKey.isMetaPressed)
                    ) {
                        onAddToGroup()
                    } else {
                        onSelect()
                    }
                },
                modifier = Modifier.height(40.dp),
            )

            EsDropdownMenuItem(
                text = "Delete",
                prefixIcon = EsIcons.Trash,
                iconColor = MaterialTheme.esColorScheme.iconPrimary,
                hintText = Key.Backspace.toString().substring(4),
                onClick = {
                    onDeleteProposalService()
                },
                modifier = Modifier.height(40.dp),
            )

            EsDropdownMenuItem(
                text = "Duplicate",
                prefixIcon = EsIcons.Add,
                iconColor = MaterialTheme.esColorScheme.iconPrimary,
                hintText = "${Key.MetaLeft.toString().substring(4)} + ${Key.D.toString().substring(4)}",
                onClick = {
                    isDropDownExpanded = false
                    onDuplicateProposalService()
                },
                modifier = Modifier.height(40.dp),
            )

            if (isSetAsPaidEnabled) {
                EsDropdownMenuItem(
                    text = "Confirmed",
                    prefixIcon = EsIcons.DollarSquare,
                    iconColor = MaterialTheme.esColorScheme.iconPrimary,
                    onClick = {
                        isDropDownExpanded = false
                        onMarkPaid()
                    },
                    enabled = isSetAsPaidEnabled,
                    modifier = Modifier.height(40.dp),
                )
            }

            if (isTotalOnlyProposal)
                EsDropdownMenuItem(
                    text = "Hide on proposal",
                    prefixIcon = EsIcons.Eye,
                    iconColor = MaterialTheme.esColorScheme.iconPrimary,
                    onClick = {
                        isDropDownExpanded = false
                        onHideOnProposal()
                    },
                    modifier = Modifier.height(40.dp),
                )
        }
    }
}